import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useLocale } from "vuetify";
import {
  localeFromPath,
  localeFromSearch,
  type PracticeLocale,
} from "common/practiceLocale";
import { usePracticeLocaleStore } from "../store/pinia/practiceLocaleStore";

const PRACTICE_ROUTE_NAMES = new Set([
  "practice",
  "practiceHe",
  "practiceBlank",
  "practiceQuestion",
]);

const HEBREW_FONT_ID = "mathboard-hebrew-font";

export function isPracticeRouteName(name: unknown): boolean {
  return typeof name === "string" && PRACTICE_ROUTE_NAMES.has(name);
}

function queryLocale(
  value: unknown,
): PracticeLocale | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === "en" || raw === "he") return raw;
  return null;
}

function ensureHebrewFont() {
  if (typeof document === "undefined") return;
  if (document.getElementById(HEBREW_FONT_ID)) return;
  const link = document.createElement("link");
  link.id = HEBREW_FONT_ID;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;700&display=swap";
  document.head.appendChild(link);
}

/**
 * Practice language for the interface and the tutor.
 * `/practice` is English and `/he/practice` is Hebrew. A saved choice applies
 * only where the URL does not name a language. Hebrew is never the default.
 * The Hebrew option appears for an Israeli or Hebrew environment, Vite dev,
 * or either practice entry URL.
 */
export function usePracticeLocale() {
  const store = usePracticeLocaleStore();
  const route = useRoute();

  const locale = computed(() => store.tutorLocale);
  const tutorLocale = computed(() => store.tutorLocale);
  const tutorHebrew = computed(() => store.tutorLocale === "he");
  const hebrewOptionAvailable = computed(() => store.optionAvailable);
  const isHebrew = computed(() => store.tutorLocale === "he");
  const isPracticeRoute = computed(() => isPracticeRouteName(route.name));
  const chromeHebrew = computed(() => isHebrew.value);
  const dir = computed(() => (isHebrew.value ? "rtl" : "ltr") as "rtl" | "ltr");
  const lang = computed(() => (isHebrew.value ? "he" : "en") as "he" | "en");

  function setLocale(next: PracticeLocale) {
    store.setLocale(next);
  }

  function refreshHebrewOption() {
    return store.refreshHebrewOption();
  }

  return {
    locale,
    tutorLocale,
    tutorHebrew,
    hebrewOptionAvailable,
    isHebrew,
    isPracticeRoute,
    chromeHebrew,
    dir,
    lang,
    setLocale,
    refreshHebrewOption,
  };
}

/**
 * Document direction and Vuetify RTL.
 * `/practice` and `/he/practice` follow the path, including under Cypress.
 * Elsewhere, `?locale=` / `?lang=` still applies outside Cypress.
 * Call once from App.vue.
 */
export function installPracticeLocaleEffects() {
  const store = usePracticeLocaleStore();
  const route = useRoute();
  const { isHebrew } = usePracticeLocale();
  const vuetifyLocale = useLocale();

  void store.refreshHebrewOption();

  watch(
    () => [route.path, route.query.locale, route.query.lang] as const,
    () => {
      const cypress = typeof window !== "undefined" && "Cypress" in window;
      const fromPath = localeFromPath(route.path);
      if (fromPath) {
        if (fromPath === "he") store.optionAvailable = true;
        store.setLocale(fromPath, { force: true });
        return;
      }
      if (cypress) return;
      const next =
        queryLocale(route.query.locale) ??
        queryLocale(route.query.lang) ??
        localeFromSearch(window.location.search);
      if (next === "he") store.optionAvailable = true;
      if (next) store.setLocale(next, { force: true });
    },
  );

  watch(
    isHebrew,
    (hebrew) => {
      vuetifyLocale.current.value = hebrew ? "he" : "en";
      if (typeof document === "undefined") return;
      document.documentElement.lang = hebrew ? "he" : "en";
      document.documentElement.dir = hebrew ? "rtl" : "ltr";
      if (hebrew) ensureHebrewFont();
    },
    { immediate: true },
  );
}
